import React, { useState } from 'react';
import { TimeSlot } from '../types';
import { generateUniqueId, validateTimeSlots } from '../utils';

interface MultipleTimeSlotsEditorProps {
    initialTimeSlots: TimeSlot[];
    onSave: (timeSlots: TimeSlot[]) => void;
    onClose: () => void;
}

export const MultipleTimeSlotsEditor: React.FC<MultipleTimeSlotsEditorProps> = ({
    initialTimeSlots,
    onSave,
    onClose
}) => {
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(
        initialTimeSlots.length > 0 ? initialTimeSlots : [{
            id: generateUniqueId(),
            startTime: '09:00',
            endTime: '18:00'
        }]
    );
    const [error, setError] = useState<string>('');

    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 6; hour <= 22; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options.push(timeString);
            }
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    const handleTimeSlotChange = (id: string, field: 'startTime' | 'endTime', value: string) => {
        setTimeSlots(prev => prev.map(slot => 
            slot.id === id ? { ...slot, [field]: value } : slot
        ));
        setError(''); // Clear error when user makes changes
    };

    const addTimeSlot = () => {
        const newSlot: TimeSlot = {
            id: generateUniqueId(),
            startTime: '14:00',
            endTime: '17:00'
        };
        setTimeSlots(prev => [...prev, newSlot]);
    };

    const removeTimeSlot = (id: string) => {
        if (timeSlots.length > 1) {
            setTimeSlots(prev => prev.filter(slot => slot.id !== id));
        }
    };

    const handleSave = () => {
        const validation = validateTimeSlots(timeSlots);
        if (validation.isValid) {
            onSave(timeSlots);
        } else {
            setError(validation.error || 'Erreur de validation');
        }
    };

    const getTotalHours = () => {
        return timeSlots.reduce((total, slot) => {
            const startMinutes = slot.startTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
            const endMinutes = slot.endTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
            return total + (endMinutes - startMinutes) / 60;
        }, 0);
    };

    return (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-4 min-w-[320px] max-w-[400px]">
            <div className="flex justify-between items-center mb-3">
                <div className="text-sm font-medium text-gray-700">
                    Gérer les créneaux horaires
                </div>
                <div className="text-xs text-blue-600 font-medium">
                    Total: {getTotalHours().toFixed(1)}h
                </div>
            </div>
            
            <div className="space-y-3 max-h-64 overflow-y-auto">
                {timeSlots.map((slot, index) => (
                    <div key={slot.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                        <div className="text-xs text-gray-500 w-4">
                            {index + 1}
                        </div>
                        <div className="flex-1">
                            <select
                                value={slot.startTime}
                                onChange={(e) => handleTimeSlotChange(slot.id, 'startTime', e.target.value)}
                                className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {timeOptions.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                        </div>
                        <div className="text-xs text-gray-400">à</div>
                        <div className="flex-1">
                            <select
                                value={slot.endTime}
                                onChange={(e) => handleTimeSlotChange(slot.id, 'endTime', e.target.value)}
                                className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {timeOptions.map(time => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </select>
                        </div>
                        {timeSlots.length > 1 && (
                            <button
                                onClick={() => removeTimeSlot(slot.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Supprimer ce créneau"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                ))}
            </div>
            
            <div className="mt-3">
                <button
                    onClick={addTimeSlot}
                    className="w-full px-3 py-2 text-xs text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 transition-colors"
                >
                    + Ajouter un créneau
                </button>
            </div>
            
            {error && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {error}
                </div>
            )}
            
            <div className="flex justify-end space-x-2 pt-3 mt-3 border-t border-gray-100">
                <button
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                >
                    Annuler
                </button>
                <button
                    onClick={handleSave}
                    className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                    Enregistrer
                </button>
            </div>
        </div>
    );
};